import { useState } from 'react'
import { NUDGGE_API_BASE_URL } from '../../lib/config'

export default function ProviderConfigForm({ config, providers, onSubmit, onCancel, loading }) {
  const [showApiKey, setShowApiKey] = useState(false)
  const [copied, setCopied] = useState(false)
  const [formData, setFormData] = useState({
    Name: config?.Name || '',
    SmsProviderId: config?.SmsProviderId || '',
    ApiKey: config?.JsonConfig?.ApiKey || '',
    BaseUrl: config?.JsonConfig?.BaseUrl || '',
    // Send-Sms / Send-Sms-Templated read the delivery-report URL from
    // JsonConfig.WebhookUrl. Older rows may still carry it as CallbackUrl.
    WebhookUrl: config?.JsonConfig?.WebhookUrl || config?.JsonConfig?.CallbackUrl || `${NUDGGE_API_BASE_URL}/functions/v1/Sms-Webhook`
  })

  const handleSubmit = (e) => {
    e.preventDefault()

    // Preserve any keys the form does not manage so an edit never drops them.
    const { CallbackUrl: _legacyCallbackUrl, ...existing } = config?.JsonConfig ?? {}
    const payload = {
      Name: formData.Name,
      SmsProviderId: formData.SmsProviderId ? Number(formData.SmsProviderId) : null,
      JsonConfig: {
        ...existing,
        ApiKey: formData.ApiKey,
        BaseUrl: formData.BaseUrl,
        WebhookUrl: formData.WebhookUrl
      }
    }

    onSubmit(payload)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleCopyApiKey = async () => {
    try {
      await navigator.clipboard.writeText(formData.ApiKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Configuration Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="Name"
          value={formData.Name}
          onChange={handleChange}
          required
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="e.g., Production Infobip"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          SMS Provider <span className="text-red-500">*</span>
        </label>
        <select
          name="SmsProviderId"
          value={formData.SmsProviderId}
          onChange={handleChange}
          required
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">-- Select Provider --</option>
          {providers?.map((provider) => (
            <option key={provider.SmsProviderId} value={provider.SmsProviderId}>
              {provider.SmsProviderName}
            </option>
          ))}
        </select>
        {(!providers || providers.length === 0) && (
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
            No providers found. The provider will be auto-assigned when you save.
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          API Key <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showApiKey ? "text" : "password"}
            name="ApiKey"
            value={formData.ApiKey}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 pr-20 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Infobip API key"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1">
            <button
              type="button"
              onClick={handleCopyApiKey}
              disabled={!formData.ApiKey}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
              tabIndex={-1}
              title="Copy to clipboard"
            >
              {copied ? (
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowApiKey(!showApiKey)}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition"
              tabIndex={-1}
              title={showApiKey ? "Hide API key" : "Show API key"}
            >
              {showApiKey ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Your Infobip API key (stored securely in JsonConfig)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Base URL <span className="text-red-500">*</span>
        </label>
        <input
          type="url"
          name="BaseUrl"
          value={formData.BaseUrl}
          onChange={handleChange}
          required
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="https://xxxxx.api.infobip.com"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Your Infobip API base URL (found in your Infobip dashboard)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Delivery Report Webhook URL
        </label>
        <input
          type="url"
          name="WebhookUrl"
          value={formData.WebhookUrl}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="https://your-domain.supabase.co/functions/v1/Sms-Webhook"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Configure this URL in your Infobip webhook settings for delivery reports
        </p>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
        >
          {loading ? 'Saving...' : config ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  )
}
